/**
 * A label to place: where its tick stands, as a fraction of the line from its
 * start; what the zman is in the day and the part of the day that belongs to,
 * as the server names them; and its measured size.
 */
export type Measured = {
  id: string
  x: number
  group: string
  part: string
  width: number
  height: number
}

/**
 * Labels that share one block under the line: their ids in time order, and
 * where the block starts, in pixels from the line's start — so the same
 * numbers serve a line read either way.
 */
export type Cluster = { ids: string[]; start: number }

type Block = {
  ids: string[]
  group: string | null
  part: string
  from: number
  to: number
  width: number
  height: number
}

/**
 * The labels on the line through today, set in one row of blocks beneath it,
 * as many as the room holds — or null, where not even one block per part of
 * the day can be set.
 *
 * Every zman starts in a block of its own. While the row cannot be set with
 * each block under its own stretch of line, the block where it fails is merged
 * with a neighbor: one that is the same thing in the day if it has one (the
 * sun coming up, the morning's deadlines, nightfall), otherwise one in the same
 * part of the day, the nearer first. So blocks are gathered only where the
 * room runs out and only as far as it needs, a block never mixes two parts of
 * the day, and every block stands under the ticks it names — until the parts
 * themselves are all that is left, which may slide off their ticks as far as
 * the room needs. `rowGap` is the room between two labels stacked in a block.
 */
export function clusterLabels(
  labels: readonly Measured[],
  lineWidth: number,
  gap: number,
  rowGap: number,
): { clusters: Cluster[]; reach: number } | null {
  if (labels.some((l) => l.width > lineWidth)) return null
  let blocks: Block[] = [...labels]
    .sort((a, b) => a.x - b.x)
    .map((l) => ({
      ids: [l.id],
      group: l.group,
      part: l.part,
      from: l.x,
      to: l.x,
      width: l.width,
      height: l.height,
    }))

  for (;;) {
    const placed = place(blocks, lineWidth, gap, true)
    if ('starts' in placed) return result(blocks, placed.starts)
    const pair = mergeAt(blocks, placed.failing)
    if (pair === null) break
    blocks = [
      ...blocks.slice(0, pair),
      merge(blocks[pair], blocks[pair + 1], rowGap),
      ...blocks.slice(pair + 2),
    ]
  }
  const placed = place(blocks, lineWidth, gap, false)
  return 'starts' in placed ? result(blocks, placed.starts) : null
}

function result(blocks: readonly Block[], starts: number[]) {
  return {
    clusters: blocks.map((b, i) => ({ ids: b.ids, start: starts[i] })),
    reach: Math.max(0, ...blocks.map((b) => b.height)),
  }
}

/**
 * Which neighboring pair to merge so the row has more room: beside a block
 * that failed where one may merge there, and anywhere otherwise — within a
 * group before within a part, the nearer pair first. Null when nothing may
 * merge.
 */
function mergeAt(blocks: readonly Block[], failing: number[]): number | null {
  const sameGroup = (i: number) =>
    blocks[i].group !== null && blocks[i].group === blocks[i + 1].group
  const samePart = (i: number) => blocks[i].part === blocks[i + 1].part
  const apart = (i: number) => blocks[i + 1].from - blocks[i].to
  const pairs = (near: number[]) =>
    near.filter((i) => i >= 0 && i < blocks.length - 1)
  const pick = (candidates: number[]) =>
    candidates.length
      ? candidates.reduce((best, i) => (apart(i) < apart(best) ? i : best))
      : null
  const local = pairs(failing.flatMap((i) => [i - 1, i]))
  const everywhere = pairs(blocks.map((_, i) => i))
  return (
    pick(local.filter(sameGroup)) ??
    pick(local.filter(samePart)) ??
    pick(everywhere.filter(sameGroup)) ??
    pick(everywhere.filter(samePart))
  )
}

function merge(a: Block, b: Block, rowGap: number): Block {
  return {
    ids: [...a.ids, ...b.ids],
    group: a.group === b.group ? a.group : null,
    part: a.part,
    from: a.from,
    to: b.to,
    width: Math.max(a.width, b.width),
    height: a.height + rowGap + b.height,
  }
}

/**
 * Where each block starts: centered under the ticks it names and kept inside
 * the line, then pushed past the one before it and pulled back inside the line
 * from the end. Where the row cannot hold them — or, where `underTicks`, where
 * a block would have to leave the stretch of line its ticks cover — it names
 * every block that fails instead.
 */
function place(
  blocks: readonly Block[],
  lineWidth: number,
  gap: number,
  underTicks: boolean,
): { starts: number[] } | { failing: number[] } {
  const starts = blocks.map((b) => {
    const middle = ((b.from + b.to) / 2) * lineWidth
    return Math.min(Math.max(middle - b.width / 2, 0), lineWidth - b.width)
  })
  for (let i = 1; i < blocks.length; i++) {
    starts[i] = Math.max(starts[i], starts[i - 1] + blocks[i - 1].width + gap)
  }
  for (let i = blocks.length - 1; i >= 0; i--) {
    const room = i === blocks.length - 1 ? lineWidth : starts[i + 1] - gap
    starts[i] = Math.min(starts[i], room - blocks[i].width)
  }
  const failing = starts.flatMap((s, i) =>
    s < 0 ||
    (underTicks &&
      (s > blocks[i].to * lineWidth ||
        s + blocks[i].width < blocks[i].from * lineWidth))
      ? [i]
      : [],
  )
  return failing.length ? { failing } : { starts }
}
